import moment from 'moment';
import { atpApi } from 'config/axios';
import Personalizer from 'personalizer';
import ATPConfigUtil from 'utils/training_plans/ATPConfigUtil';

// map workoutPlanId to plan type
export const PlanTypes = {
    '1': '5K',
    '2': '10K',
    '3': 'HALFMARATHON'
};

export const calculateDayDifference = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);

    return end.diff(start, 'days');
};

export const calculateWeekDifference = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);

    return end.diff(start, 'weeks');
};

export const getRunWithTimeGoalDisplayWithPace = (timeInSeconds, planTypeId, i18next) => {
    let pace = getPaceFromTimeGoal(timeInSeconds, planTypeId);
    return (
        pace +
        ' ' +
        i18next.t(`units:${Personalizer.getPaceUnitKey(true)}`) +
        ' ' +
        i18next.t('training_plans:label_pace_lowercase')
    );
};

export const getPaceFromTimeGoal = (timeInSeconds, planTypeId) => {
    let distance = ATPConfigUtil.planDistance[ATPConfigUtil.getPlanType(planTypeId)],
        pace = Personalizer.personalizePace(distance / timeInSeconds);
    return pace;
};

export const isActive = (atpPlan) => {
    //active includes the plan being paused
    return atpPlan && !atpPlan.planCompleted && !atpPlan.quit;
};

export const getPlanName = (id, planTypes, i18next) => {
    for (let i = 0, len = planTypes.length; i < len; i += 1) {
        if (planTypes[i] && planTypes[i].workoutPlanTypeId === id) {
            return planTypes[i].name + ' ' + i18next.t('training_plans:training_plan_plan_label');
        }
    }
    //default: Adaptive Training Plan
    return i18next.t('training_plans:atp_training_plan_label');
};

export const PlanStatusTypes = {
    PAUSED: 'PAUSED',
    QUIT: 'QUIT',
    COMPLETED: 'COMPLETED',
    STARTED: 'STARTED'
};

export const getPlanStatus = (atpPlan) => {
    if (atpPlan.planCompleted && (atpPlan.quit === null || atpPlan.quit.state === false)) {
        return PlanStatusTypes.COMPLETED;
    } else if (atpPlan.quit && atpPlan.quit.state) {
        return PlanStatusTypes.QUIT;
    } else if (atpPlan.pause && atpPlan.pause.state) {
        return PlanStatusTypes.PAUSED;
    } else {
        return PlanStatusTypes.STARTED;
    }
};

export const getArticleContentByType = (atpFeedPartialContent, type) => {
    if (Object.keys(atpFeedPartialContent).length > 0) {
        const articleItems = atpFeedPartialContent.articleItems;
        return articleItems.find(function(item) {
            return item.fields !== null && item.fields[type] !== null;
        });
    }
    return null;
};

export const ActionTypes = {
    PAUSE: 'PAUSE',
    IGNORE: 'IGNORE',
    RESCHEDULE_RACE: 'RESCHEDULE_RACE',
    CHANGE_PLAN: 'CHANGE_PLAN',
    CHANGE_PACE: 'CHANGE_PACE',
    PACE: 'PACE',
    SET_LONG_DAY: 'SET_LONG_DAY'
};

export const getActionOptionByType = (atpPlan, type) => {
    let actionOption = [];

    if (atpPlan && atpPlan.action && atpPlan.action.options) {
        actionOption = atpPlan.action.options.filter((option) => {
            return option.actionOption.type === type;
        });
    }

    return actionOption.length > 0 ? actionOption[0] : null;
};

/**
 * Returns an object that is required for updating a plan which uses the plans values
 *
 * @param {*} atpPlan
 */
export const getUpdatedPlan = (atpPlan) => {
    return {
        planTypeId: atpPlan.workoutPlanTypeId,
        selectedGoal: atpPlan.athletePlanGoal.goal,
        goalTimeSeconds: atpPlan.goalTimeSeconds,
        selectedPaceGoalTime: atpPlan.goalTimeSeconds,
        selectedRace: {
            raceDate: atpPlan.athleteRace.raceDay,
            raceTitle: atpPlan.athleteRace.raceName,
            raceRegistrationUrl: atpPlan.athleteRace.raceUrl
        },
        selectedWorkoutDays: atpPlan.workoutCalendarDays,
        selectedWorkoutPlanId: atpPlan.workoutPlanId,
        selectedWorkoutsPerWeek: atpPlan.workoutsPerWeek,
        athletePlanId: atpPlan.athletePlanId
    };
};

export const getIndexByName = (atpPlan, miniViewName) => {
    let index = -1;

    switch (miniViewName) {
        case 'training_goal':
            index = 4;
            break;
        case 'personal_coach':
            index = 5;
            break;
        case 'workouts_per_week_title':
            index = 6;
            break;
        case 'days_available':
            index = 7;
            break;
        case 'long_run_day':
            index = 8;
            break;
        case 'atp_race':
            index = 9;
            break;
        case 'event_date':
            index = 12;
            break;
    }

    return index;
};

export const OverViewSteps = {
    overview: 1,
    distance: 2,
    pace: 3,
    goal: 4
};

export const CoachSteps = {
    coach: 5
};

export const TrainingPreferenceSteps = {
    WorkoutsPerWeek: 6,
    DaysAvailable: 7,
    LongRunDay: 8
};

export const EventSteps = {
    event: 9
};

export const EventDateSteps = {
    eventDate: 12
};

export const getSectionByStep = (Steps, step) => {
    let section = null;
    for (const [key, value] of Object.entries(Steps)) {
        if (value === step) {
            section = key;
        }
    }
    return section;
};

export const createPlan = (plan) => {
    return atpApi.post('/atp/setup/create', plan);
};

export const activePlans = () => {
    return atpApi.get('/atp/athlete/active');
};

export const updatePlan = (athletePlanId, plan) => {
    return atpApi.put(`/atp/setup/update/${athletePlanId}`, plan);
};

export const getUpdatedCoaches = ({
    athletePlanId: athletePlanId,
    goal: goal,
    raceGoalTime: raceGoalTime,
    lang: lang
}) => {
    return atpApi.get(
        `/atp/setup/plans/change?athletePlanId=${athletePlanId}&goal=${goal}&raceGoalTime:${raceGoalTime}&lang=${lang}`
    );
};

export const getAtpPlan = (athletePlanId, lang) => {
    return atpApi.get(`/atp/athlete/plan?athletePlanId=${athletePlanId}&lang=${lang}`);
};

// +/- 5% of distances in meters
export const MinMaxRanges = {
    '5K': {
        startDistance: 4750,
        endDistance: 5250
    },
    '10K': {
        startDistance: 9500,
        endDistance: 10500
    },
    HALFMARATHON: {
        startDistance: 20042,
        endDistance: 22152
    },
    MARATHON: {
        startDistance: 40085,
        endDistance: 44305
    }
};

export const formatPlanForUpdate = (atpPlan) => {
    return {
        planTypeId: atpPlan.workoutPlanTypeId,
        selectedGoal: atpPlan.athletePlanGoal.goal,
        selectedPaceGoalTime: atpPlan.goalTimeSeconds,
        selectedRace: {
            raceDate: atpPlan.athleteRace.raceDay,
            raceTitle: atpPlan.athleteRace.raceName,
            raceRegistrationUrl: atpPlan.athleteRace.raceUrl,
            raceEventId: atpPlan.athleteRace.raceEventId,
            raceEventUuid: atpPlan.athleteRace.raceEventUuid
        },
        selectedWorkoutDays: atpPlan.workoutCalendarDays,
        selectedWorkoutPlanId: atpPlan.workoutPlanId,
        selectedWorkoutsPerWeek: atpPlan.workoutsPerWeek,
        athletePlanId: atpPlan.athletePlanId,
        prePlanTrainingPace: atpPlan.prePlanTrainingPace,
        prePlanWeeklyMileage: atpPlan.prePlanWeeklyMileage,
        longRunDay: atpPlan.longRunDay
    };
};

export const CoachTrainingPlanId = 'COACH_ATP';

export const isCoachTrainingPlan = (event) => {
    return (
        !!event.eventCustomization?.trainingPlanId && event.eventCustomization?.trainingPlanType === CoachTrainingPlanId
    );
};

export const hidePlan = ({ athletePlanId }) => {
    return atpApi.post(`/atp/athlete/completed/hide?athletePlanId=${athletePlanId}`);
};

export const unPausePlan = ({ reason, athletePlanId }) => {
    return atpApi.post(`/atp/athlete/unpause/${reason}?athletePlanId=${athletePlanId}`);
};
